import { JMCHero } from "@components/JMCHero/JMCHero";
import analyticstracker from "@jmc/analyticstracker";
import { CarouselSlideTracking } from "@jmc/core/src/components/CarouselSlideTracking/index";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { ImageCarousel } from "@jmc/solid-design-system/src/components/organisms/ImageCarousel/ImageCarousel";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CarouselSize, CarouselTextColor, CarouselViewMode, CMSFootnotes } from "@types";
import classnames from "classnames";
import React from "react";

import { CarouselSlide } from "./slides/CarouselSlide";
import style from "./style.module.scss";

interface PropTypes {
    interval: { automatic: boolean; speed: number };
    size?: CarouselSize;
    fullWidth?: boolean;
    isPageHeader?: boolean;
    slides: React.ComponentProps<typeof CarouselSlide>[];
    placement?: string;
    footnotes?: CMSFootnotes[];
    loading?: "eager" | "lazy";
}

export const JMCCarousel = ({
    interval,
    size = CarouselSize.MEDIUM,
    fullWidth = false,
    isPageHeader = false,
    slides,
    placement,
    footnotes,
    loading = "lazy",
}: PropTypes): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    const typeBackground = !!slides?.find((slide) => slide?.viewMode === CarouselViewMode.BACKGROUND);

    const textColorMap = new Map<CarouselTextColor, string>([
        ["white", "--white"],
        ["dark", "---dark"],
        ["primary", "--primary"],
        ["secondary", "--secondary"],
        ["accent 1", "--accent"],
        ["accent", "--accent"],
        ["accent 2", "--accent-2"],
        ["accent-2", "--accent-2"],
    ]);

    // Event tracking on carousel button clickk
    const eventTracking = (name: string, action: string, index: number): void => {
        const info = {
            name: name,
            placement: placement,
            position: index + 1,
            type: "carousel",
            action: action,
        };
        analyticstracker().trackEvent({
            event: EventTypes.CONTENTBLOCK_CLICK,
            info: info,
        });
    };

    const getSlideTitle = (slide: React.ComponentProps<typeof CarouselSlide>): string => {
        return (
            slide?.title ||
            slide?.image?.description ||
            slide?.image?.title?.substring(0, slide?.image?.title?.lastIndexOf("."))
        );
    };

    // on image carousel left-arrow, right-arow, carousel-indicator click
    const onActionButtonClick = (action: string, index: number): void => {
        const matchedSlide = slides[index];
        eventTracking(getSlideTitle(matchedSlide), action, index);
    };

    let isTesting = false;
    if (typeof document !== "undefined" && document.cookie.indexOf("absoluteUnit=") !== -1) {
        isTesting = true;
    }

    if (isPageHeader) {
        fullWidth = false;
    }

    return (
        <div
            id={fullWidth ? "jmc-carousel-full" : "jmc-carousel"}
            className={classnames(
                style.carousel,
                style[size.toLowerCase()],
                typeBackground && style.background,
                fullWidth && style.fullWidth,
                isPageHeader && style.pageHeader,
            )}
        >
            <ImageCarousel
                fullWidth={fullWidth}
                interval={interval}
                roundedCorners={false}
                pauseOnHover
                actionButtonClick={onActionButtonClick}
                isTransparent={jnjFullBranded}
            >
                {slides.map((slide: React.ComponentProps<typeof CarouselSlide>, index: number) => {
                    const slideTitle = getSlideTitle(slide);
                    if (slide.viewMode === CarouselViewMode.BACKGROUND && !jnjFullBranded) {
                        return (
                            <div className={isTesting ? style.slideTest : style.slide} key={`carousel-item-${index}`}>
                                <CarouselSlideTracking
                                    name={slideTitle}
                                    type={"carousel"}
                                    placement={placement}
                                    position={index}
                                    key={`slide-${slideTitle}-${index}`}
                                >
                                    <JMCHero
                                        key={`carousel-hero-${index}`}
                                        hero={{
                                            display_title: slide.title,
                                            hero_body: slide.text,
                                            call_to_action:
                                                slide.buttons?.length > 0
                                                    ? slide.buttons.map((button) => ({ button }))
                                                    : null,
                                            background_image: slide.image,
                                            display_options: {
                                                overlay: slide.overlay,
                                                align_content: slide.alignContent?.toLowerCase(),
                                                align_content_vertically: slide.alignContentVertically?.toLowerCase(),
                                                title_color:
                                                    slide.titleColor === CarouselTextColor.DARK
                                                        ? "--dark"
                                                        : textColorMap.get(slide.titleColor.toLowerCase()),
                                                body_color:
                                                    slide.textColor === CarouselTextColor.DARK
                                                        ? "--dark"
                                                        : textColorMap.get(slide.textColor.toLowerCase()),
                                                background_color: slide.backgroundColor,
                                                image_focus: { image_focus: slide.imageFocus },
                                            },
                                        }}
                                        height="calc(100% - var(--spacing-L) - var(--spacing-XL))"
                                        titleComponent={isPageHeader && index === 0 ? "h1" : "h3"}
                                        footnotes={footnotes}
                                        loading={index === 0 ? loading : "lazy"}
                                    />
                                </CarouselSlideTracking>
                            </div>
                        );
                    } else {
                        return (
                            <div className={isTesting ? style.slideTest : style.slide} key={`carousel-item-${index}`}>
                                <CarouselSlideTracking
                                    name={slideTitle}
                                    type={"carousel"}
                                    placement={placement}
                                    position={index}
                                    key={`slide-${slideTitle}-${index}`}
                                >
                                    <CarouselSlide
                                        key={`carousel-slide-${index}`}
                                        {...slide}
                                        size={size}
                                        placement={placement}
                                        footnotes={footnotes}
                                        loading={index === 0 ? loading : "lazy"}
                                        headerVariant={jnjFullBranded && isPageHeader}
                                    />
                                </CarouselSlideTracking>
                            </div>
                        );
                    }
                })}
            </ImageCarousel>
        </div>
    );
};

export default JMCCarousel;
